import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import styles from "./About.module.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const About = () => {
  const { lang } = useLang();

  const values = [
    { title: t("about.value1Title", lang), description: t("about.value1Desc", lang) },
    { title: t("about.value2Title", lang), description: t("about.value2Desc", lang) },
    { title: t("about.value3Title", lang), description: t("about.value3Desc", lang) },
    { title: t("about.value4Title", lang), description: t("about.value4Desc", lang) },
  ];

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className={styles.heroArea}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className={styles.title}>{t("about.title", lang)}</h1>
        <p className={styles.subtitle}>{t("about.subtitle", lang)}</p>
      </motion.div>

      <motion.div
        className={styles.content}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <motion.div className={styles.storySection} variants={fadeUp}>
          <h2 className={styles.sectionHeading}>{t("about.storyTitle", lang)}</h2>
          <p className={styles.paragraph}>{t("about.storyP1", lang)}</p>
          <p className={styles.paragraph}>{t("about.storyP2", lang)}</p>
          <p className={styles.paragraph}>{t("about.storyP3", lang)}</p>
        </motion.div>

        <motion.div className={styles.storySection} variants={fadeUp}>
          <h2 className={styles.sectionHeading}>{t("about.whatSetsTitle", lang)}</h2>
          <p className={styles.paragraph}>{t("about.whatSetsP1", lang)}</p>
          <p className={styles.paragraph}>{t("about.whatSetsP2", lang)}</p>
        </motion.div>
      </motion.div>

      <motion.div
        className={styles.valuesSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.sectionHeading} variants={fadeUp}>
          {t("about.valuesTitle", lang)}
        </motion.h2>
        <div className={styles.valuesGrid}>
          {values.map((value, i) => (
            <motion.div
              key={i}
              className={styles.valueCard}
              variants={fadeUp}
              whileHover={{ y: -4, borderColor: "var(--accent)" }}
            >
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        className={styles.missionSection}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <h2 className={styles.sectionHeading}>{t("about.missionTitle", lang)}</h2>
        <p className={styles.missionText}>
          <strong>{t("about.missionBold", lang)}</strong>{" "}
          {t("about.missionText", lang)}
        </p>
      </motion.div>

      <motion.div
        className={styles.ctaBox}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        <h3>{t("about.ctaTitle", lang)}</h3>
        <p>{t("about.ctaDesc", lang)}</p>
        <Link to="/contact" className={styles.ctaButton}>
          {t("about.ctaButton", lang)}
        </Link>
      </motion.div>
    </motion.div>
  );
};

export default About;
